import { memo, useCallback, useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { MainStackParams } from '../../common/screen_params';
import useGlobalStyles from '../../common/style_context';
import { FocusStateType, useFocusState } from '../../common/hooks';
import { observer } from 'mobx-react-lite';
import TvPressable from '../tv_pressable';
import KeyWrapper from '../key_wrapper';
import { commonStyles } from '../../common/styles';
import { computed } from 'mobx';
import AppColors from '../../common/colors';
import { VoidCb } from '../../types/others';

type Props = MainStackParams['MainDialogTv'] & {
  submit: (action?: string) => void;
};

const dialogStyles = [
  `w=500,bg=${AppColors.containerGray},p=24,br=16`,
  'fs=32,fw=700,c=white,tc,mb=36',
  'fs=32,fw=700,c=white,tc',
];

const MainDialogTv = memo<Props>(
  ({ title, text, actions, defaultAction, submit }) => {
    const focusState = useFocusState(actions.length);

    const [container, titleStyle, textStyle] = useGlobalStyles(dialogStyles);

    const up = useCallback((keyUp?: boolean) => {
      if (keyUp) return;
      focusState.decrement();
    }, []);
    const down = useCallback((keyUp?: boolean) => {
      if (keyUp) return;
      focusState.increment();
    }, []);
    const confirm = useCallback(() => {
      const f = focusState.focused;
      if (f === undefined) return;
      submit(actions[f].action);
    }, [actions, submit]);
    const cancel = useCallback(
      () => submit(defaultAction),
      [defaultAction, submit],
    );

    const actionElements = useMemo(
      () =>
        actions.map((e, i) => (
          <MainDialogItem
            key={i}
            text={e.text}
            index={i}
            focusState={focusState}
            onPress={confirm}
          />
        )),
      [actions, confirm],
    );

    return (
      <View style={styles.container}>
        <TvPressable onPress={cancel} style={commonStyles.fill} />
        <KeyWrapper
          up={up}
          down={down}
          confirm={confirm}
          cancel={cancel}
          style={container}>
          <Text style={titleStyle}>{title}</Text>
          <Text style={textStyle}>{text}</Text>
          {actionElements}
        </KeyWrapper>
      </View>
    );
  },
);

type DialogAction = {
  text: string;
  index: number;
  focusState: FocusStateType;
  onPress?: VoidCb;
};

const itemStyles = [
  'ac,mt=16,pv=12',
  `fill,bg=${AppColors.mainBlue},br=12`,
  'fill,bg=black,br=12',
  'fs=28,fw=500,c=white',
];

const MainDialogItem = observer<DialogAction>(
  ({ text, index, focusState, onPress }) => {
    const focused = computed(() => focusState.focused == index).get();

    const [container, focusedContainer, unfocusedContainer, textStyle] =
      useGlobalStyles(itemStyles);

    const press = useCallback(() => {
      focusState.focused = index;
      onPress?.();
    }, []);

    return (
      <TvPressable onPress={press} style={container}>
        <View style={focused ? focusedContainer : unfocusedContainer} />
        <Text style={textStyle}>{text}</Text>
      </TvPressable>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default MainDialogTv;
